import React, { useState, useEffect, useMemo } from "react";
import { Plus, Send, BarChart3, ChevronRight, ChevronLeft, Check, X, Download, Trash2, Edit, TrendingUp, LayoutList, Table as TableIcon, AlignJustify, Eye } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { generatePDF } from "../utils/pdfGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PARS = [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 3, 4, 5, 4, 4, 3, 4, 5];

const emptyHoleStats = (index) => ({
  hole: index + 1,
  par: DEFAULT_PARS[index],
  fairway: null,
  green_regulation: null,
  putts: null,
  chip: "",
  bunker: "",
  pitching: "",
  up_and_down: "",
  total: null
});

const parseRatio = (str) => {
  if (typeof str !== 'string' || !str) return { made: 0, attempts: 0 };
  if (!str.includes("/")) {
    return { made: 0, attempts: 0 };
  }
  const [made, attempts] = str.split("/").map(Number);
  return { made: made || 0, attempts: attempts || 0 };
};

const safeFormatDate = (dateStr) => {
  try {
    if (!dateStr) return "Нет даты";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Неверная дата";
    return format(date, "d MMMM yyyy", { locale: ru });
  } catch (e) {
    return "Ошибка даты";
  }
};

export default function StatsTab({ user }) {
  const [stats, setStats] = useState([]);
  const [receivedStats, setReceivedStats] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedStatsId, setSelectedStatsId] = useState(null);
  const [selectedStatsComments, setSelectedStatsComments] = useState("");
  const [viewMode, setViewMode] = useState("my");
  
  // Input Mode: "table", "cards", "list"
  const [inputMode, setInputMode] = useState("table");
  const [currentHoleIndex, setCurrentHoleIndex] = useState(0);
  
  // For Read-Only View
  const [viewOnlyStats, setViewOnlyStats] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  const [newRound, setNewRound] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    course_name: "",
    comments: "",
    holes: Array.from({ length: 18 }, (_, i) => emptyHoleStats(i))
  });

  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchStats();
    fetchCoaches();
    if (isAdmin) fetchReceivedStats();
  }, [isAdmin]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error("Fetch stats error:", error);
      toast.error("Ошибка загрузки статистики");
    } finally {
      setLoading(false);
    }
  };

  const fetchReceivedStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/stats/received`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReceivedStats(response.data);
    } catch (error) {
      console.error("Error fetching received stats:", error);
    }
  };

  const fetchCoaches = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/coaches`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCoaches(response.data);
    } catch (error) {
      console.error("Error fetching coaches:", error);
    }
  };

  const updateHole = (index, field, value) => {
    setNewRound(prev => {
      const holes = [...prev.holes];
      holes[index] = { ...holes[index], [field]: value };
      if (field === "par" && value === 3) {
        holes[index].fairway = null;
      }
      return { ...prev, holes };
    });
  };

  const resetForm = () => {
    setNewRound({
      date: format(new Date(), "yyyy-MM-dd"),
      course_name: "",
      comments: "",
      holes: Array.from({ length: 18 }, (_, i) => emptyHoleStats(i))
    });
    setCurrentHoleIndex(0);
  };

  const handleSaveRound = async () => {
    if (!newRound.course_name) {
      toast.error("Введите название поля");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/stats`, {
        date: newRound.date,
        course_name: newRound.course_name,
        comments: newRound.comments,
        holes: newRound.holes
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Раунд сохранен");
      setShowAddDialog(false);
      resetForm();
      fetchStats();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Ошибка сохранения: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateRound = async () => {
    if (!newRound.course_name) {
      toast.error("Введите название поля");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/stats/${newRound.stats_id}`, {
        date: newRound.date,
        course_name: newRound.course_name,
        comments: newRound.comments,
        holes: newRound.holes
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Раунд обновлен");
      setShowAddDialog(false);
      resetForm();
      fetchStats();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Ошибка обновления: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleSendToCoach = async (coachId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/stats/send-to-coach`, {
        stats_id: selectedStatsId,
        coach_id: coachId,
        send_as_message: true,
        comments: selectedStatsComments
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Статистика отправлена тренеру");
      setShowSendDialog(false);
      setSelectedStatsId(null);
      setSelectedStatsComments("");
      fetchStats();
    } catch (error) {
      toast.error("Ошибка отправки");
    }
  };

  const handleDeleteStats = async (statsId) => {
    if (!window.confirm("Удалить статистику раунда?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/stats/${statsId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Статистика удалена");
      fetchStats();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const handleEditStats = (stat) => {
    setNewRound({
      stats_id: stat.stats_id,
      date: stat.date,
      course_name: stat.course_name,
      comments: stat.comments || "",
      holes: stat.holes.map((h, i) => ({
        ...emptyHoleStats(i),
        ...h
      }))
    });
    setShowAddDialog(true);
  };

  const handleViewStats = (stat) => {
    setViewOnlyStats(stat);
    setShowViewDialog(true);
  };

  const openSendDialog = (statsId, comments) => {
    setSelectedStatsId(statsId);
    setSelectedStatsComments(comments || "");
    setShowSendDialog(true);
  };

  const handleExportPDF = async (stat) => {
    const toastId = toast.loading("Создание PDF...");
    try {
      await generatePDF(stat);
      toast.dismiss(toastId);
      toast.success("PDF сохранен");
    } catch (e) {
      toast.dismiss(toastId);
      toast.error("Ошибка PDF");
    }
  };

  // Calculate totals
  const totals = useMemo(() => {
    const holes = newRound.holes || [];
    if (!Array.isArray(holes) || holes.length === 0) {
      return {
        fw: "0/0", fwPct: 0,
        gir: "0/0", girPct: 0,
        putts: 0, avgPutts: "0.0",
        chip: "0/0", chipPct: 0,
        bunker: "0/0", bunkerPct: 0,
        pitching: "0/0", pitchPct: 0,
        upDown: "0/0", udPct: 0,
        score: 0,
        par: 0
      };
    }
    
    const par4and5 = holes.filter(h => h.par >= 4);
    const fwHits = par4and5.filter(h => h.fairway === true).length;
    const fwTotal = par4and5.length;
    const fwPct = fwTotal > 0 ? Math.round((fwHits / fwTotal) * 100) : 0;
    
    const girHits = holes.filter(h => h.green_regulation === true).length;
    const girTotal = holes.filter(h => h.green_regulation !== null).length || 18;
    const girPct = girTotal > 0 ? Math.round((girHits / girTotal) * 100) : 0;
    
    const totalPutts = holes.reduce((sum, h) => sum + (h.putts || 0), 0);
    const holesWithPutts = holes.filter(h => h.putts !== null && h.putts !== undefined).length;
    const avgPutts = holesWithPutts > 0 ? (totalPutts / holesWithPutts).toFixed(1) : "0.0";
    
    let chipMade = 0, chipAttempts = 0;
    let bunkerMade = 0, bunkerAttempts = 0;
    let pitchMade = 0, pitchAttempts = 0;
    let udMade = 0, udAttempts = 0;
    
    holes.forEach(h => {
      const chip = parseRatio(h.chip);
      chipMade += chip.made; chipAttempts += chip.attempts;
      const bunk = parseRatio(h.bunker);
      bunkerMade += bunk.made; bunkerAttempts += bunk.attempts;
      const pitch = parseRatio(h.pitching);
      pitchMade += pitch.made; pitchAttempts += pitch.attempts;
      const ud = parseRatio(h.up_and_down);
      udMade += ud.made; udAttempts += ud.attempts;
    });
    
    return {
      fw: `${fwHits}/${fwTotal}`, fwPct,
      gir: `${girHits}/${girTotal}`, girPct,
      putts: totalPutts, avgPutts,
      chip: `${chipMade}/${chipAttempts}`,
      chipPct: chipAttempts > 0 ? Math.round((chipMade / chipAttempts) * 100) : 0,
      bunker: `${bunkerMade}/${bunkerAttempts}`,
      bunkerPct: bunkerAttempts > 0 ? Math.round((bunkerMade / bunkerAttempts) * 100) : 0,
      pitching: `${pitchMade}/${pitchAttempts}`,
      pitchPct: pitchAttempts > 0 ? Math.round((pitchMade / pitchAttempts) * 100) : 0,
      upDown: `${udMade}/${udAttempts}`,
      udPct: udAttempts > 0 ? Math.round((udMade / udAttempts) * 100) : 0,
      score: holes.reduce((sum, h) => sum + (h.total || 0), 0),
      par: holes.reduce((sum, h) => sum + h.par, 0)
    };
  }, [newRound.holes]);

  const displayedStats = viewMode === "my" ? (stats || []) : (receivedStats || []);

  const globalStats = useMemo(() => {
    if (!Array.isArray(stats) || stats.length === 0) return null;
    
    const count = stats.length;
    let totalScore = 0;
    let totalPutts = 0;
    let totalFwHits = 0;
    let totalFwTotal = 0;
    let totalGirHits = 0;
    let totalGirTotal = 0;
    
    stats.forEach(s => {
      if (s.totals) {
        totalScore += s.totals.total_score || 0;
        totalPutts += s.totals.total_putts || 0;
        totalFwHits += s.totals.fairway_hits || 0;
        totalFwTotal += s.totals.fairway_total || 0;
        totalGirHits += s.totals.gir_hits || 0;
        totalGirTotal += s.totals.gir_total || 0;
      }
    });
    
    return {
      avgScore: (totalScore / count).toFixed(1),
      avgPutts: (totalPutts / count).toFixed(1),
      fwPct: totalFwTotal > 0 ? Math.round((totalFwHits / totalFwTotal) * 100) : 0,
      girPct: totalGirTotal > 0 ? Math.round((totalGirHits / totalGirTotal) * 100) : 0,
      rounds: count
    };
  }, [stats]);

  const renderHoleCard = (index) => {
    const hole = newRound.holes[index];
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <h3 className="font-heading font-bold text-xl">Лунка {hole.hole}</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Par</span>
            <select
              value={hole.par}
              onChange={(e) => updateHole(index, "par", parseInt(e.target.value))}
              className="h-8 w-14 text-center bg-gray-50 border border-gray-200 rounded-lg font-semibold"
            >
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Score</Label>
            <Input 
              type="number" 
              min="1" 
              max="15" 
              value={hole.total || ""} 
              onChange={(e) => updateHole(index, "total", e.target.value ? parseInt(e.target.value) : null)} 
              className="h-12 text-center text-lg font-bold bg-gray-50"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Putts</Label>
            <Input 
              type="number" 
              min="0" 
              max="10" 
              value={hole.putts || ""} 
              onChange={(e) => updateHole(index, "putts", e.target.value ? parseInt(e.target.value) : null)} 
              className="h-12 text-center text-lg bg-gray-50"
            />
          </div>
        </div>

        <div className="flex gap-2">
          {hole.par !== 3 && (
            <button
              onClick={() => updateHole(index, "fairway", hole.fairway === true ? false : true)}
              className={`flex-1 h-12 rounded-xl flex items-center justify-center gap-2 transition-all ${
                hole.fairway === true ? "bg-golf-green text-white" : 
                hole.fairway === false ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              <span className="text-sm font-medium">Fairway</span>
              {hole.fairway === true ? <Check className="w-4 h-4" /> : 
               hole.fairway === false ? <X className="w-4 h-4" /> : ""}
            </button>
          )}
          
          <button
            onClick={() => updateHole(index, "green_regulation", hole.green_regulation === true ? false : true)}
            className={`flex-1 h-12 rounded-xl flex items-center justify-center gap-2 transition-all ${
              hole.green_regulation === true ? "bg-golf-green text-white" : 
              hole.green_regulation === false ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            <span className="text-sm font-medium">GIR</span>
            {hole.green_regulation === true ? <Check className="w-4 h-4" /> : 
             hole.green_regulation === false ? <X className="w-4 h-4" /> : ""}
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {[
            { key: "chip", label: "Chip" },
            { key: "bunker", label: "Bunk" },
            { key: "pitching", label: "Pitch" },
            { key: "up_and_down", label: "U&D" }
          ].map(field => (
            <div key={field.key} className="space-y-1">
              <Label className="text-[10px] text-center w-full block text-gray-500">{field.label}</Label>
              <Input 
                value={hole[field.key] || ""} 
                onChange={(e) => updateHole(index, field.key, e.target.value)} 
                placeholder="0/0" 
                className="h-9 text-center text-xs px-1"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderListRow = (hole, index) => {
    return (
      <div key={index} className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="bg-gray-100 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">{hole.hole}</span>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">Par</span>
              <select
                value={hole.par}
                onChange={(e) => updateHole(index, "par", parseInt(e.target.value))}
                className="h-6 w-10 text-center bg-gray-50 border border-gray-200 rounded text-xs"
              >
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
             <Input 
                type="number" 
                min="1" 
                max="15" 
                value={hole.total || ""} 
                onChange={(e) => updateHole(index, "total", e.target.value ? parseInt(e.target.value) : null)} 
                className="h-8 w-14 text-center font-bold bg-gray-50 p-0"
                placeholder="Scr"
              />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-2">
           <div className="flex items-center gap-2">
             <span className="text-xs w-8">Putts</span>
             <Input 
                type="number" 
                min="0" 
                max="10" 
                value={hole.putts || ""} 
                onChange={(e) => updateHole(index, "putts", e.target.value ? parseInt(e.target.value) : null)} 
                className="h-8 w-full text-center bg-gray-50 p-0"
              />
           </div>
           <div className="flex justify-end gap-1">
              {hole.par !== 3 && (
                <button
                  onClick={() => updateHole(index, "fairway", hole.fairway === true ? false : true)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                    hole.fairway === true ? "bg-golf-green text-white" : 
                    hole.fairway === false ? "bg-red-500 text-white" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  <span className="text-[10px] font-bold">FW</span>
                </button>
              )}
              <button
                onClick={() => updateHole(index, "green_regulation", hole.green_regulation === true ? false : true)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  hole.green_regulation === true ? "bg-golf-green text-white" : 
                  hole.green_regulation === false ? "bg-red-500 text-white" : "bg-gray-100 text-gray-400"
                }`}
              >
                <span className="text-[10px] font-bold">GIR</span>
              </button>
           </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {["chip", "bunker", "pitching", "up_and_down"].map(key => (
             <Input 
                key={key}
                value={hole[key] || ""} 
                onChange={(e) => updateHole(index, key, e.target.value)} 
                placeholder={key.substring(0,3).toUpperCase()} 
                className="h-7 text-center text-[10px] px-0"
              />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="stats-tab">
      {isAdmin && (
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => setViewMode("my")}
            variant={viewMode === "my" ? "default" : "outline"}
            className={`flex-1 h-10 rounded-full ${viewMode === "my" ? "bg-golf-green hover:bg-golf-green-hover" : ""}`}
          >
            Мои раунды
          </Button>
          <Button
            onClick={() => setViewMode("received")}
            variant={viewMode === "received" ? "default" : "outline"}
            className={`flex-1 h-10 rounded-full ${viewMode === "received" ? "bg-golf-green hover:bg-golf-green-hover" : ""}`}
          >
            Полученные
          </Button>
        </div>
      )}

      {viewMode === "my" && (
        <>
          {globalStats && (
            <div className="bg-gradient-to-br from-golf-green to-emerald-800 rounded-2xl p-4 mb-4 text-white shadow-lg relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3 opacity-90">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-medium">Средние показатели за все раунды</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center bg-white/10 rounded-xl p-2 backdrop-blur-sm">
                    <p className="text-xs opacity-80 mb-1">Score</p>
                    <p className="font-bold text-lg">{globalStats.avgScore}</p>
                  </div>
                  <div className="text-center bg-white/10 rounded-xl p-2 backdrop-blur-sm">
                    <p className="text-xs opacity-80 mb-1">Putts</p>
                    <p className="font-bold text-lg">{globalStats.avgPutts}</p>
                  </div>
                  <div className="text-center bg-white/10 rounded-xl p-2 backdrop-blur-sm">
                    <p className="text-xs opacity-80 mb-1">FW</p>
                    <p className="font-bold text-lg">{globalStats.fwPct}%</p>
                  </div>
                  <div className="text-center bg-white/10 rounded-xl p-2 backdrop-blur-sm">
                    <p className="text-xs opacity-80 mb-1">GIR</p>
                    <p className="font-bold text-lg">{globalStats.girPct}%</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            </div>
          )}

          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="w-full mb-4 h-12 bg-golf-green hover:bg-golf-green-hover rounded-xl shadow-sm" data-testid="add-stats-btn">
                <Plus className="w-5 h-5 mr-2" />
                Добавить статистику
              </Button>
            </DialogTrigger>
            {/* 
              Simplified scroll handling:
              - Removed shadcn ScrollArea component which often causes height issues on mobile.
              - Used native div scrolling with max-h-[90dvh] (better for mobile viewports).
              - Added -webkit-overflow-scrolling: touch via overflow-y-auto.
            */}
            <DialogContent className="w-[98vw] max-w-4xl h-auto max-h-[90dvh] p-0 overflow-y-auto flex flex-col bg-white">
              <div className="sticky top-0 z-20 bg-white border-b border-gray-100 p-4 flex flex-row items-center justify-between">
                <DialogTitle className="font-heading text-lg">
                  {newRound.stats_id ? "Редактировать" : "Новый раунд"}
                </DialogTitle>
                <div className="flex bg-gray-100 rounded-lg p-1 mr-8">
                  <button 
                    onClick={() => setInputMode("table")}
                    className={`p-1.5 rounded-md transition-colors ${inputMode === "table" ? "bg-white shadow-sm" : "text-gray-500"}`}
                    title="Таблица"
                  >
                    <TableIcon className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setInputMode("list")}
                    className={`p-1.5 rounded-md transition-colors ${inputMode === "list" ? "bg-white shadow-sm" : "text-gray-500"}`}
                    title="Список"
                  >
                    <AlignJustify className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setInputMode("cards")}
                    className={`p-1.5 rounded-md transition-colors ${inputMode === "cards" ? "bg-white shadow-sm" : "text-gray-500"}`}
                    title="Карточки"
                  >
                    <LayoutList className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-sm">Дата</Label>
                      <Input
                        type="date"
                        value={newRound.date}
                        onChange={(e) => setNewRound({ ...newRound, date: e.target.value })}
                        className="h-11 bg-apple-gray border-transparent rounded-xl w-full text-left justify-start block"
                        style={{ textAlign: 'left' }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Название поля</Label>
                      <Input
                        value={newRound.course_name}
                        onChange={(e) => setNewRound({ ...newRound, course_name: e.target.value })}
                        placeholder="Например: Agalarov Golf"
                        className="h-11 bg-apple-gray border-transparent rounded-xl w-full"
                      />
                    </div>
                  </div>

                  {inputMode === "cards" && (
                    <div className="space-y-4">
                      {renderHoleCard(currentHoleIndex)}
                      
                      <div className="flex justify-between items-center bg-gray-50 p-2 rounded-xl">
                        <Button 
                          variant="ghost" 
                          disabled={currentHoleIndex === 0}
                          onClick={() => setCurrentHoleIndex(prev => prev - 1)}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <span className="font-semibold">{currentHoleIndex + 1} / 18</span>
                        <Button 
                          variant="ghost" 
                          disabled={currentHoleIndex === 17}
                          onClick={() => setCurrentHoleIndex(prev => prev + 1)}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {inputMode === "list" && (
                    <div className="space-y-2">
                      {newRound.holes.map((hole, index) => renderListRow(hole, index))}
                    </div>
                  )}

                  {inputMode === "table" && (
                    <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="overflow-x-auto -webkit-overflow-scrolling-touch" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <div className="min-w-[800px]">
                          <div className="grid grid-cols-11 gap-px text-[10px] sm:text-xs font-semibold text-center bg-golf-green text-white p-2 sticky top-0 z-10">
                            <div className="sticky left-0 bg-golf-green z-20">#</div>
                            <div>Par</div>
                            <div>FW</div>
                            <div>GIR</div>
                            <div>Putts</div>
                            <div>Chip</div>
                            <div>Bunk</div>
                            <div>Pitch</div>
                            <div>U&D</div>
                            <div>Total</div>
                            <div>+/-</div>
                          </div>

                          {newRound.holes.map((hole) => (
                            <div 
                              key={hole.hole}
                              className={`grid grid-cols-11 gap-px items-center text-xs py-1.5 border-b border-gray-50 ${hole.hole % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                            >
                              <div className="text-center font-semibold text-gray-500 sticky left-0 bg-inherit z-10 shadow-[1px_0_0_0_rgba(0,0,0,0.05)]">
                                {hole.hole}
                              </div>
                              
                              <div className="flex justify-center px-1">
                                <select
                                  value={hole.par}
                                  onChange={(e) => updateHole(hole.hole - 1, "par", parseInt(e.target.value))}
                                  className="w-full h-7 text-center bg-white border border-gray-200 rounded cursor-pointer text-xs"
                                >
                                  <option value={3}>3</option>
                                  <option value={4}>4</option>
                                  <option value={5}>5</option>
                                </select>
                              </div>
                              
                              <div className="flex justify-center">
                                {hole.par === 3 ? (
                                  <span className="text-gray-300">-</span>
                                ) : (
                                  <button
                                    onClick={() => updateHole(hole.hole - 1, "fairway", hole.fairway === true ? false : true)}
                                    className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
                                      hole.fairway === true ? "bg-golf-green text-white shadow-sm" : 
                                      hole.fairway === false ? "bg-red-500 text-white shadow-sm" : "bg-white border border-gray-200"
                                    }`}
                                  >
                                    {hole.fairway === true ? <Check className="w-3.5 h-3.5" /> : 
                                     hole.fairway === false ? <X className="w-3.5 h-3.5" /> : ""}
                                  </button>
                                )}
                              </div>

                              <div className="flex justify-center">
                                <button
                                  onClick={() => updateHole(hole.hole - 1, "green_regulation", hole.green_regulation === true ? false : true)}
                                  className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
                                    hole.green_regulation === true ? "bg-golf-green text-white shadow-sm" : 
                                    hole.green_regulation === false ? "bg-red-500 text-white shadow-sm" : "bg-white border border-gray-200"
                                  }`}
                                >
                                  {hole.green_regulation === true ? <Check className="w-3.5 h-3.5" /> : 
                                   hole.green_regulation === false ? <X className="w-3.5 h-3.5" /> : ""}
                                </button>
                              </div>

                              <div className="px-1"><Input type="number" min="0" max="10" value={hole.putts || ""} onChange={(e) => updateHole(hole.hole - 1, "putts", e.target.value ? parseInt(e.target.value) : null)} className="h-7 text-center p-0 text-xs" /></div>
                              <div className="px-1"><Input value={hole.chip || ""} onChange={(e) => updateHole(hole.hole - 1, "chip", e.target.value)} placeholder="0/0" className="h-7 text-center p-0 text-xs" /></div>
                              <div className="px-1"><Input value={hole.bunker || ""} onChange={(e) => updateHole(hole.hole - 1, "bunker", e.target.value)} placeholder="0/0" className="h-7 text-center p-0 text-xs" /></div>
                              <div className="px-1"><Input value={hole.pitching || ""} onChange={(e) => updateHole(hole.hole - 1, "pitching", e.target.value)} placeholder="0/0" className="h-7 text-center p-0 text-xs" /></div>
                              <div className="px-1"><Input value={hole.up_and_down || ""} onChange={(e) => updateHole(hole.hole - 1, "up_and_down", e.target.value)} placeholder="0/0" className="h-7 text-center p-0 text-xs" /></div>
                              <div className="px-1"><Input type="number" min="1" max="15" value={hole.total || ""} onChange={(e) => updateHole(hole.hole - 1, "total", e.target.value ? parseInt(e.target.value) : null)} className="h-7 text-center p-0 text-xs font-bold" /></div>
                              
                              <div className={`text-center font-bold text-xs ${hole.total && hole.total < hole.par ? "text-red-500" : hole.total && hole.total > hole.par ? "text-blue-500" : "text-gray-400"}`}>
                                {hole.total ? (hole.total - hole.par > 0 ? `+${hole.total - hole.par}` : hole.total - hole.par === 0 ? "E" : hole.total - hole.par) : ""}
                              </div>
                            </div>
                          ))}

                          <div className="grid grid-cols-11 gap-px items-center text-[10px] sm:text-xs bg-gray-900 text-white font-semibold py-2">
                            <div className="text-center sticky left-0 bg-gray-900 z-10">Tot</div>
                            <div className="text-center text-gray-400">{totals.par}</div>
                            <div className="text-center leading-tight">{totals.fw}<br/><span className="text-[9px] text-gray-400">{totals.fwPct}%</span></div>
                            <div className="text-center leading-tight">{totals.gir}<br/><span className="text-[9px] text-gray-400">{totals.girPct}%</span></div>
                            <div className="text-center leading-tight">{totals.putts}<br/><span className="text-[9px] text-gray-400">ср.{totals.avgPutts}</span></div>
                            <div className="text-center leading-tight">{totals.chip}<br/><span className="text-[9px] text-gray-400">{totals.chipPct}%</span></div>
                            <div className="text-center leading-tight">{totals.bunker}<br/><span className="text-[9px] text-gray-400">{totals.bunkerPct}%</span></div>
                            <div className="text-center leading-tight">{totals.pitching}<br/><span className="text-[9px] text-gray-400">{totals.pitchPct}%</span></div>
                            <div className="text-center leading-tight">{totals.upDown}<br/><span className="text-[9px] text-gray-400">{totals.udPct}%</span></div>
                            <div className="text-center text-base">{totals.score}</div>
                            <div className="text-center">{totals.score > 0 ? (totals.score - totals.par > 0 ? `+${totals.score - totals.par}` : totals.score - totals.par) : "-"}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-sm font-medium">Комментарии к раунду</Label>
                    <Textarea
                      value={newRound.comments}
                      onChange={(e) => setNewRound({ ...newRound, comments: e.target.value })}
                      placeholder="Как прошла игра? Что получилось, над чем стоит поработать..."
                      className="bg-apple-gray border-transparent rounded-xl resize-none min-h-[80px]"
                    />
                  </div>
              </div>
              
              <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 mt-auto">
                <Button onClick={newRound.stats_id ? handleUpdateRound : handleSaveRound} className="w-full h-12 bg-golf-green hover:bg-golf-green-hover rounded-xl text-lg font-medium shadow-lg shadow-golf-green/20">
                  {newRound.stats_id ? "Обновить раунд" : "Сохранить раунд"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Stats List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : displayedStats.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><BarChart3 className="w-8 h-8 text-apple-text-tertiary" /></div>
          <p className="text-apple-text-secondary">Нет сохраненных раундов</p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedStats.map((stat) => {
            const fw = stat.totals?.fairway_hits || 0;
            const fwTotal = stat.totals?.fairway_total || 14;
            const fwPct = fwTotal > 0 ? Math.round((fw / fwTotal) * 100) : 0;
            const gir = stat.totals?.gir_hits || 0;
            const girTotal = stat.totals?.gir_total || 18;
            const girPct = girTotal > 0 ? Math.round((gir / girTotal) * 100) : 0;
            const avgPutts = stat.totals?.avg_putts || ((stat.totals?.total_putts || 0) / 18).toFixed(1);
            
            return (
              <div key={stat.stats_id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading font-semibold text-lg">{stat.course_name}</h3>
                    <p className="text-sm text-apple-text-secondary">{format(new Date(stat.date), "d MMMM yyyy", { locale: ru })}</p>
                    {viewMode === "received" && <p className="text-xs text-golf-green mt-1">от {stat.user_name}</p>}
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-2xl text-golf-green">{stat.totals?.total_score || 0}</p>
                    <p className="text-xs text-apple-text-secondary">{(stat.totals?.total_score || 0) - (stat.totals?.total_par || 72) > 0 ? "+" : ""}{(stat.totals?.total_score || 0) - (stat.totals?.total_par || 72)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div className="bg-apple-gray rounded-xl p-2 text-center">
                    <p className="text-xs text-apple-text-secondary">FW</p>
                    <p className="font-semibold text-sm">{fw}/{fwTotal}</p>
                    <p className="text-[10px] text-golf-green">{fwPct}%</p>
                  </div>
                  <div className="bg-apple-gray rounded-xl p-2 text-center">
                    <p className="text-xs text-apple-text-secondary">GIR</p>
                    <p className="font-semibold text-sm">{gir}/{girTotal}</p>
                    <p className="text-[10px] text-golf-green">{girPct}%</p>
                  </div>
                  <div className="bg-apple-gray rounded-xl p-2 text-center">
                    <p className="text-xs text-apple-text-secondary">Putts</p>
                    <p className="font-semibold text-sm">{stat.totals?.total_putts || 0}</p>
                    <p className="text-[10px] text-golf-green">ср. {avgPutts}</p>
                  </div>
                  <div className="bg-apple-gray rounded-xl p-2 text-center">
                    <p className="text-xs text-apple-text-secondary">Score</p>
                    <p className="font-semibold">{stat.totals?.total_score || 0}</p>
                  </div>
                </div>

                {stat.comments && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Комментарии:</p>
                    <p className="text-sm text-gray-700">{stat.comments}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={() => handleExportPDF(stat)} variant="outline" size="icon" className="h-10 w-10 rounded-xl border-gray-200" title="Скачать PDF">
                    <Download className="w-4 h-4" />
                  </Button>
                  
                  {viewMode === "received" ? (
                    <Button onClick={() => handleViewStats(stat)} variant="outline" className="flex-1 h-10 rounded-xl border-golf-green text-golf-green hover:bg-golf-green/10">
                      <Eye className="w-4 h-4 mr-2" />
                      Просмотр
                    </Button>
                  ) : (
                    <>
                      <Button onClick={() => handleEditStats(stat)} variant="outline" size="icon" className="h-10 w-10 rounded-xl border-gray-200" title="Редактировать">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleDeleteStats(stat.stats_id)} variant="outline" size="icon" className="h-10 w-10 rounded-xl border-red-200 text-red-500 hover:bg-red-50" title="Удалить">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => openSendDialog(stat.stats_id, stat.comments)} variant="outline" className="flex-1 h-10 rounded-xl border-golf-green text-golf-green hover:bg-golf-green/10">
                        <Send className="w-4 h-4 mr-2" />
                        Тренеру
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Кому отправить?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-4">
            {coaches.length === 0 ? (
              <p className="text-apple-text-secondary text-center py-4">Нет тренеров</p>
            ) : (
              coaches.map((coach) => (
                <button
                  key={coach.user_id}
                  onClick={() => handleSendToCoach(coach.user_id)}
                  className="w-full p-4 rounded-xl bg-apple-gray hover:bg-gray-200 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-golf-green/10 flex items-center justify-center">
                      <span className="text-golf-green font-semibold">{coach.first_name?.[0]}{coach.last_name?.[0]}</span>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold">{coach.first_name} {coach.last_name}</p>
                      <p className="text-sm text-apple-text-secondary">{coach.email}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-apple-text-tertiary" />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="w-[98vw] max-w-4xl h-auto max-h-[90vh] p-0 sm:p-6 flex flex-col">
          <DialogHeader className="p-4 sm:p-0 border-b sm:border-none shrink-0">
            <DialogTitle className="font-heading text-lg">
              Просмотр раунда: {viewOnlyStats?.course_name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-0">
            {viewOnlyStats && (
              <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', maxHeight: '60vh' }}>
                  <div className="min-w-[800px]">
                      <div className="grid grid-cols-11 gap-px text-[10px] sm:text-xs font-semibold text-center bg-golf-green text-white p-2">
                        <div>#</div><div>Par</div><div>FW</div><div>GIR</div><div>Putts</div><div>Chip</div><div>Bunk</div><div>Pitch</div><div>U&D</div><div>Tot</div><div>+/-</div>
                      </div>
                      {viewOnlyStats.holes.map((hole, index) => (
                        <div key={index} className={`grid grid-cols-11 gap-px items-center text-xs py-1.5 border-b border-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                          <div className="text-center font-semibold">{hole.hole}</div>
                          <div className="text-center">{hole.par}</div>
                          <div className="text-center">{hole.par === 3 ? "-" : (hole.fairway ? "✓" : hole.fairway === false ? "✗" : "-")}</div>
                          <div className="text-center">{hole.green_regulation ? "✓" : hole.green_regulation === false ? "✗" : "-"}</div>
                          <div className="text-center">{hole.putts}</div>
                          <div className="text-center">{hole.chip}</div>
                          <div className="text-center">{hole.bunker}</div>
                          <div className="text-center">{hole.pitching}</div>
                          <div className="text-center">{hole.up_and_down}</div>
                          <div className="text-center font-bold">{hole.total}</div>
                          <div className="text-center">{(hole.total - hole.par) > 0 ? `+${hole.total - hole.par}` : (hole.total - hole.par)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
}
